/**
 * Twilio Voice SDK identity encoding — the wire format between the mobile
 * Voice SDK and the TwiML webhook, so it has exactly one definition.
 *
 * The Capacitor plugin we use (@capgo/capacitor-twilio-voice) doesn't expose
 * a way to pass custom TwiML params at `makeCall` time. To still scope a
 * call to the property the staff is acting under, we pack `propertyId` into
 * the Voice SDK identity:
 *
 *     identity = `<memberId>__<propertyId>`
 *
 * The TwiML webhook receives `from = client:<memberId>__<propertyId>` on
 * outgoing calls, splits to recover both halves, and uses the propertyId to
 * look up the property's phone integration (which provides the callerId).
 * The mobile app uses `encodeVoiceIdentity` to construct the `to` URI for
 * staff-to-staff calls, and status callbacks for child legs deliver the same
 * compound back — same parse applies.
 *
 * The separator `__` was chosen because it's allowed by Twilio identity
 * char restrictions (alphanumeric + `_` `.` `-` `+` `\` `=`) and is highly
 * unlikely to appear in a UUID.
 */

const SEPARATOR = "__";

export function encodeVoiceIdentity(
  memberId: string,
  propertyId: string
): string {
  return `${memberId}${SEPARATOR}${propertyId}`;
}

export interface DecodedVoiceIdentity {
  memberId: string;
  propertyId: string | null;
}

/**
 * Parse a Voice SDK identity back into its parts. Accepts both the bare
 * identity (`a__b`) and the `client:` URI form (`client:a__b`) since
 * Twilio webhooks deliver the latter.
 *
 * Returns `propertyId = null` for tokens that predate this scheme — those
 * staff sessions just won't have property-scoped outbound calls until a
 * new token is minted, rather than failing hard.
 */
export function decodeVoiceIdentity(
  identity: string | null | undefined
): DecodedVoiceIdentity | null {
  if (!identity) {
    return null;
  }
  const stripped = identity.startsWith("client:")
    ? identity.slice("client:".length)
    : identity;
  const idx = stripped.indexOf(SEPARATOR);
  if (idx < 0) {
    return { memberId: stripped, propertyId: null };
  }
  const memberId = stripped.slice(0, idx);
  const propertyId = stripped.slice(idx + SEPARATOR.length);
  if (!(memberId && propertyId)) {
    return null;
  }
  return { memberId, propertyId };
}
